import React, { useState, useEffect, useRef } from 'react';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth, API_ENDPOINTS } from '../config/api';

interface Doc {
  id: string;
  filename: string;
  summary: string;
  full_text: string;
  created_at: string;
}

// ─── Chat panel ──────────────────────────────────────────────────────────────
interface ChatMessage { role: 'user' | 'ai'; text: string; }

function DocChat({ doc }: { doc: Doc }) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages([]);
    setInput('');
  }, [doc.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const token = await currentUser?.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${API_ENDPOINTS.CHAT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, context: doc.full_text }),
        credentials: 'include',
        mode: 'cors',
      });
      const data = await res.json();
      setMessages(m => [...m, { role: 'ai', text: data.response || 'No response.' }]);
    } catch {
      setMessages(m => [...m, { role: 'ai', text: 'Failed to get a response. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ height: '340px' }}>
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 pb-2">
        {messages.length === 0 && (
          <p className="text-sm theme-text-secondary text-center pt-8">
            Ask anything about <span className="font-medium theme-text-primary">{doc.filename}</span>
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-sm rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === 'user'
                ? 'text-white'
                : 'theme-surface border theme-border theme-text-primary'
            }`}
              style={m.role === 'user' ? { background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' } : {}}
            >
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="theme-surface border theme-border rounded-2xl px-4 py-2.5 text-sm theme-text-secondary animate-pulse">
              Thinking…
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2 pt-3 border-t theme-border mt-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask a question…"
          disabled={loading}
          className="flex-1 rounded-xl px-4 py-2.5 text-sm theme-input focus:outline-none"
        />
        <button
          type="submit"
          disabled={!input.trim() || loading}
          className="rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40 transition-all"
          style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}
        >
          Send
        </button>
      </form>
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────
function DocDetail({ doc, onDelete }: { doc: Doc; onDelete: (id: string) => void }) {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState<'summary' | 'chat'>('summary');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownload = async () => {
    const token = await currentUser?.getIdToken();
    const res = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${API_ENDPOINTS.DOWNLOAD(doc.id)}`,
      { headers: { Authorization: `Bearer ${token}` }, credentials: 'include', mode: 'cors' }
    );
    if (!res.ok) { alert('Download failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = doc.filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(doc.summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await fetchWithAuth(API_ENDPOINTS.DELETE_DOCUMENT(doc.id), { method: 'DELETE' });
      onDelete(doc.id);
    } catch {
      alert('Failed to delete document.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const date = doc.created_at
    ? new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h2 className="text-lg font-bold theme-text-primary truncate">{doc.filename}</h2>
          <p className="text-xs theme-text-secondary mt-0.5">{date} · {doc.summary.split(' ').length.toLocaleString()} words</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={handleCopy} title="Copy summary"
            className="p-2 rounded-lg border theme-border theme-text-secondary hover:text-indigo-500 transition-colors text-xs flex items-center gap-1">
            {copied ? '✓ Copied' : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <button onClick={handleDownload} title="Download PDF"
            className="p-2 rounded-lg border theme-border theme-text-secondary hover:text-indigo-500 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} title="Delete document"
              className="p-2 rounded-lg border border-red-200 text-red-400 hover:text-red-600 hover:border-red-400 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-red-500 font-medium">Delete?</span>
              <button onClick={handleDelete} disabled={deleting}
                className="px-2.5 py-1 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                {deleting ? '…' : 'Yes'}
              </button>
              <button onClick={() => setConfirmDelete(false)}
                className="px-2.5 py-1 rounded-lg border theme-border text-xs font-medium theme-text-secondary hover:theme-text-primary">
                No
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
        {(['summary', 'chat'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className="flex-1 py-2 text-sm font-semibold rounded-lg capitalize transition-all duration-200"
            style={tab === t
              ? { background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }
              : { color: 'var(--text-secondary)' }
            }>
            {t === 'chat' ? '💬 Chat' : '📄 Summary'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'summary' ? (
          <p className="text-sm leading-relaxed theme-text-primary whitespace-pre-wrap">{doc.summary}</p>
        ) : (
          <DocChat doc={doc} />
        )}
      </div>
    </div>
  );
}

// ─── Document list item ───────────────────────────────────────────────────────
function DocCard({ doc, selected, onClick }: { doc: Doc; selected: boolean; onClick: () => void }) {
  const date = doc.created_at
    ? new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  const preview = doc.summary?.slice(0, 120) || '';

  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-4 rounded-xl transition-all duration-150 border"
      style={{
        background: selected ? 'linear-gradient(135deg,rgba(79,70,229,0.1),rgba(124,58,237,0.08))' : 'var(--bg-secondary)',
        borderColor: selected ? 'var(--accent-primary)' : 'var(--border-color)',
        boxShadow: selected ? '0 0 0 2px rgba(79,70,229,0.2)' : 'none',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-lg"
          style={{ background: selected ? 'linear-gradient(135deg,#4f46e5,#7c3aed)' : 'var(--bg-tertiary)' }}>
          📄
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate theme-text-primary">{doc.filename}</p>
          <p className="text-xs theme-text-secondary mt-0.5">{date}</p>
          {preview && (
            <p className="text-xs theme-text-secondary mt-1.5 line-clamp-2 leading-relaxed">{preview}…</p>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const { currentUser } = useAuth();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Doc | null>(null);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetchWithAuth(API_ENDPOINTS.SUMMARIES, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        const data = await res.json();
        setDocs(data.summaries || []);
      } catch {
        setError('Failed to load documents.');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser]);

  const handleDelete = (id: string) => {
    setDocs(d => d.filter(doc => doc.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const filtered = docs
    .filter(d => d.filename.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sort === 'newest' ? tb - ta : ta - tb;
    });

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navigation />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Page title */}
        <div className="mb-6">
          <h1 className="text-3xl font-extrabold theme-text-primary">My Documents</h1>
          <p className="mt-1 text-sm theme-text-secondary">
            {docs.length} document{docs.length !== 1 ? 's' : ''} · click any to read its summary or chat with it
          </p>
        </div>

        {loading ? (
          <div className="space-y-3 animate-pulse">
            {[1,2,3].map(i => (
              <div key={i} className="h-20 rounded-xl" style={{ background: 'var(--border-color)' }} />
            ))}
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm p-4 rounded-xl border border-red-200 bg-red-50">{error}</div>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center gap-4">
            <span className="text-5xl">📂</span>
            <p className="font-semibold theme-text-primary">No documents yet</p>
            <p className="text-sm theme-text-secondary">Upload a PDF on the Summarize page to get started.</p>
            <a href="/home" className="mt-2 text-sm font-semibold theme-link">Go to Summarize →</a>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6" style={{ minHeight: '70vh' }}>
            {/* ── Left: document list ── */}
            <div className="lg:w-80 flex-shrink-0 flex flex-col gap-3">
              {/* Search + sort */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 theme-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
                  </svg>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search documents…"
                    className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl theme-input focus:outline-none"
                  />
                </div>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value as 'newest' | 'oldest')}
                  className="text-sm rounded-xl px-3 py-2.5 theme-input focus:outline-none cursor-pointer"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>

              {/* List */}
              <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                {filtered.length === 0 ? (
                  <p className="text-sm theme-text-secondary text-center py-8">No results for "{search}"</p>
                ) : filtered.map(doc => (
                  <DocCard
                    key={doc.id}
                    doc={doc}
                    selected={selected?.id === doc.id}
                    onClick={() => setSelected(doc)}
                  />
                ))}
              </div>
            </div>

            {/* ── Right: detail panel ── */}
            <div className="flex-1 theme-card p-6 overflow-hidden flex flex-col">
              {selected ? (
                <DocDetail
                  key={selected.id}
                  doc={selected}
                  onDelete={handleDelete}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
                  <span className="text-5xl">👈</span>
                  <p className="font-semibold theme-text-primary">Select a document</p>
                  <p className="text-sm theme-text-secondary max-w-xs">
                    Choose a document from the list to read its AI summary or chat with it.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
